import type { APIError } from "core/entities";
import type {
  CentrixPathsByMethod,
  ExtractSuccessItemType,
  ExtractSuccessType,
  ParametersProperty,
} from "core/entities/APITypes";
import { useLocale } from "core/hooks/useLocale";
import { useCallback, useMemo } from "react";
import useSWRInfinite from "swr/infinite";
import { buildCentrixUrl } from "../utils/buildCentrixUrl";
import { useJsonFetcherWithHeaders } from "./useJsonFetcherWithHeaders";

export type UseCentrixApiInfiniteArg<P extends CentrixPathsByMethod<"get">> = {
  path: P;
  swrOptions?: Parameters<
    typeof useSWRInfinite<ExtractSuccessType<P>, APIError>
  >[2];
  shouldFetch?: boolean;
  maxResultCount: number;
} & ParametersProperty<P, "get">;

/**
 * Custom hook to fetch data from the Centrix API.
 *
 * @example
 * ```tsx
 * const {
 *   data, // the data fetched from the API
 *   isLoading, // true if the data is being fetched
 *   isError, // contains the error if the fetch failed
 *   mutate // function manage the data. more info: https://swr.vercel.app/docs/mutation
 * } = useCentrixApi({
 *   path: "/api/app/payment-request" // or any other
 *   parameters: {
 *     query: { // these parameters will be added to the query string of the url
 *       filter: "clientName",
 *       locale: "en"
 *     },
 *   swrOptions: {}, // see https://swr.vercel.app/docs/api.en-US#options
 *   shouldFetch: true, // if false, the hook will not fetch the data
 *   fetchAll: false, // if true, the hook will fetch all paginated data. this option should should only be used if endpoint supports pagination. default is false
 *   });
 *
 * if (isLoading) return <div>Loading...</div>; // handle loading state
 * if (isError) return <ErrorComponent apiError={isError} />; // handle error state
 * return (<div>{JSON.stringify(data)}</div>); // render the data
 *
 * ```
 */
export function useCentrixApiInfinite<P extends CentrixPathsByMethod<"get">>({
  path,
  parameters,
  swrOptions,
  shouldFetch = true,
  maxResultCount,
}: UseCentrixApiInfiniteArg<P>) {
  const locale = useLocale();
  const jsonFetcher = useJsonFetcherWithHeaders();

  const getKey = useCallback(
    (index: number, previousPageData: ExtractSuccessType<P>) => {
      if (!shouldFetch) {
        return null;
      }

      if (
        previousPageData &&
        typeof previousPageData === "object" &&
        "items" in previousPageData &&
        Array.isArray(previousPageData?.items) &&
        previousPageData.items.length < maxResultCount
      ) {
        return null;
      }

      return buildCentrixUrl({
        path,
        parameters: {
          ...(parameters as any),
          query: {
            ...(parameters as any)?.query,
            maxResultCount,
            skipCount: index * maxResultCount,
          },
        },
        locale,
      });
    },
    [path, parameters, locale, maxResultCount, shouldFetch]
  );

  const { data, error, mutate, isValidating, setSize, size } = useSWRInfinite<
    ExtractSuccessType<P>,
    APIError
  >(getKey, jsonFetcher, swrOptions);

  const isLoadingInitialData = !error && !data;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === "undefined");

  const processedData = useMemo(() => {
    if (error) {
      return {
        items: [] as ExtractSuccessItemType<P>[],
        totalCount: 0,
        currentCount: 0,
        showLoadMore: false,
      };
    }
    const totalCount = (data as any)?.[0]?.totalCount ?? 0;
    const items = Array.isArray(data)
      ? data?.flatMap<ExtractSuccessItemType<P>>((entry) =>
          typeof entry === "object" &&
          entry &&
          "items" in entry &&
          Array.isArray(entry.items)
            ? entry.items
            : []
        )
      : [];
    const currentCount = items.length;
    const showLoadMore = currentCount < totalCount;

    function loadMore() {
      if (currentCount >= totalCount || isLoadingMore || isLoadingInitialData) {
        return;
      }
      setSize((currentSize) => currentSize + 1);
    }

    return {
      items,
      currentCount,
      totalCount,
      showLoadMore,
      loadMore,
    };
  }, [data, error, isLoadingMore, isLoadingInitialData, setSize]);

  return {
    isLoadingInitialData,
    isLoadingMore,
    isError: error,
    isValidating,
    mutate,
    setSize,
    size,
    data,
    error,
    ...processedData,
  };
}
